import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { BookOpen, X, ArrowRight, ArrowLeft, CheckCircle2, CheckCheck, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGuidedTour } from "@/hooks/use-guided-tour";
import { GUIDED_TOUR_STEPS } from "@/lib/guided-tour-config";

const Z = 9000;
const TOOLTIP_W = 320;
const RING_PAD = 7;

/** Split "/settings#section-foo" into ["/settings", "section-foo"]. */
function splitHash(path: string): [string, string | null] {
  const i = path.indexOf("#");
  if (i === -1) return [path, null];
  return [path.slice(0, i), path.slice(i + 1)];
}

// Human-readable labels for sidebar nav hrefs used in tour steps.
const NAV_LABELS: Record<string, string> = {
  "/quotes": "Quotes",
  "/invoices": "Invoices",
};

// ── Step completion labels ────────────────────────────────────────────────────
const STEP_COMPLETE_LABELS: Record<string, string> = {
  "create-quote": "Quote created!",
  "add-line-item": "Line item added!",
  "mark-sent": "Quote sent!",
  "convert-invoice": "Invoice created!",
  "send-invoice": "Invoice sent!",
  "record-payment": "Payment recorded!",
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export function GuidedTourOverlay() {
  const { active, index, total, step, finished, refs, pendingAdvance, stepEventCount, confirmAdvance, next, prev, exit, complete } = useGuidedTour({ drive: true });
  const [location, navigate] = useLocation();
  const isMobile = useIsMobile();

  // ── Ring: positioned via RAF loop directly on the DOM node ───────────────
  const ringRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const [targetFound, setTargetFound] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // "up" = element is above viewport, "down" = below, null = visible or not found
  const [scrollHint, setScrollHint] = useState<"up" | "down" | null>(null);
  const prevDialogOpenRef = useRef(false);

  // Horizontal anchor for scroll hint — tracks the target element's centre-X
  // so the hint appears over the sidebar when the target is a sidebar nav item,
  // not centre-screen.
  const scrollHintXRef = useRef(typeof window !== "undefined" ? window.innerWidth / 2 : 400);

  // ── Sidebar nav mode ─────────────────────────────────────────────────────
  const navSidebarHref = step?.navSidebarHref;
  const isOnNavSection = navSidebarHref ? location.startsWith(navSidebarHref) : true;
  const inSidebarNavMode = !!(navSidebarHref && !isOnNavSection);
  const navLabel = navSidebarHref ? (NAV_LABELS[navSidebarHref] ?? navSidebarHref) : "";

  // The RAF loop's ring target.
  // • Desktop + nav-mode   → spotlight the sidebar nav item (nav-/path)
  // • Mobile  + nav-mode   → spotlight the hamburger FAB (mobile-menu-btn)
  // • Normal               → spotlight the step's own target element
  const ringTargetAttr = inSidebarNavMode
    ? (isMobile ? "mobile-menu-btn" : `nav-${navSidebarHref}`)
    : (step?.targetAttr ?? "");

  // Tooltip side: "right" by default, overridden by step config.
  const tooltipSide = step?.tooltipSide ?? "right";

  // ── Transition overlay ────────────────────────────────────────────────────
  const [transition, setTransition] = useState<{ label: string; nextTitle: string } | null>(null);
  const prevLocationRef = useRef(location);
  const prevStepIdRef = useRef<string | null>(null);

  // ── Exit prompt ───────────────────────────────────────────────────────────
  const [exitPrompt, setExitPrompt] = useState(false);

  // ── Navigation: move to the page this step lives on ──────────────────────
  useEffect(() => {
    if (!active || !step) return undefined;
    if (inSidebarNavMode) return undefined;
    const path = step.resolvePath(refs);
    if (!path) return undefined;
    const [base, hash] = splitHash(path);
    if (base !== location) {
      navigate(base);
    }
    if (hash) {
      const t = window.setTimeout(() => {
        if (window.location.hash !== `#${hash}`) {
          window.location.hash = hash;
        } else {
          window.dispatchEvent(new HashChangeEvent("hashchange"));
        }
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [active, step?.id, location, inSidebarNavMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Page-transition notification ─────────────────────────────────────────
  useEffect(() => {
    if (!active || !step) return;
    const prevLoc = prevLocationRef.current;
    const prevId = prevStepIdRef.current;

    if (prevId !== null && prevId !== step.id && prevLoc !== location) {
      setTransition({ label: step.label, nextTitle: step.title });
      const t = window.setTimeout(() => setTransition(null), 2200);
      return () => window.clearTimeout(t);
    }

    prevLocationRef.current = location;
    prevStepIdRef.current = step.id;
    return undefined;
  }, [active, step?.id, location]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { prevLocationRef.current = location; }, [location]);
  useEffect(() => { prevStepIdRef.current = step?.id ?? null; }, [step?.id]);

  // ── RAF loop: smooth ring tracking + click-blocker updates ──────────────
  useEffect(() => {
    if (!active || !step || finished) {
      setTargetFound(false);
      setScrollHint(null);
      cancelAnimationFrame(rafRef.current);
      return;
    }

    // Local mirrors of React state — avoids setState on every frame.
    let localScrollHint: "up" | "down" | null = null;
    let localTargetFound = false;

    const tick = () => {
      // On mobile in sidebar-nav-mode, switch dynamically between the FAB and
      // the actual nav item based on whether the drawer is currently open.
      // Reading document.body.dataset here (set by Layout) means we react on
      // every frame without needing a React re-render to change ringTargetAttr.
      let activeTarget = ringTargetAttr;
      if (inSidebarNavMode && isMobile && navSidebarHref) {
        activeTarget = document.body.dataset.mobileSidebar === "open"
          ? `nav-${navSidebarHref}`
          : "mobile-menu-btn";
      }
      const el = document.querySelector<HTMLElement>(`[data-tour-target="${activeTarget}"]`);
      const ring = ringRef.current;

      // Detect open Radix Dialog OR AlertDialog only — intentionally excludes
      // [data-radix-popper-content-wrapper] which also matches tooltips/popovers
      // and would hide the overlay whenever any button is hovered.
      const hasOpenDialog = !!document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      );
      if (hasOpenDialog !== prevDialogOpenRef.current) {
        prevDialogOpenRef.current = hasOpenDialog;
        setIsDialogOpen(hasOpenDialog);
      }

      if (!ring) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (el && !hasOpenDialog) {
        const r = el.getBoundingClientRect();
        // Element is "visible" if any part of it is in the viewport AND the element
        // has a non-zero render size. We also verify via elementFromPoint that it
        // isn't clipped inside a scroll container (e.g. the sidebar).
        const inViewport = r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
          && r.right > 0 && r.left < window.innerWidth;
        // Visibility check: is the element's centre actually hit-testable?
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const hitEl = (cx >= 0 && cy >= 0 && cx < window.innerWidth && cy < window.innerHeight)
          ? document.elementFromPoint(cx, cy)
          : null;
        // Accept if the hit element is the target or a descendant of it.
        const actuallyVisible = inViewport && hitEl != null && (el === hitEl || el.contains(hitEl));

        if (actuallyVisible) {
          const hTop    = r.top    - RING_PAD;
          const hLeft   = r.left   - RING_PAD;
          const hRight  = r.right  + RING_PAD;
          const hBottom = r.bottom + RING_PAD;

          // Update ring position
          ring.style.top    = `${hTop}px`;
          ring.style.left   = `${hLeft}px`;
          ring.style.width  = `${r.width  + RING_PAD * 2}px`;
          ring.style.height = `${r.height + RING_PAD * 2}px`;
          ring.style.opacity = "1";

          // Clear scroll hint
          if (localScrollHint !== null) {
            localScrollHint = null;
            setScrollHint(null);
          }
          // Mark found
          if (!localTargetFound) {
            localTargetFound = true;
            setTargetFound(true);
          }
        } else {
          ring.style.opacity = "0";

          // Determine scroll direction for the hint.
          // For elements clipped inside a scroll container (e.g. sidebar overflow)
          // getBoundingClientRect may return coords within viewport but the element
          // is hidden by overflow. Use r.bottom < 0 / r.top >= vh for window scroll,
          // otherwise check if it's inside the sidebar (left < 300px) and clipped.
          let hint: "up" | "down";
          if (r.bottom < 0) {
            hint = "up";
          } else if (r.top >= window.innerHeight) {
            hint = "down";
          } else {
            // Element is within viewport rect but not hit-testable → clipped
            // by an overflow container. Determine scroll direction by position.
            hint = cy < window.innerHeight / 2 ? "up" : "down";
          }
          if (localScrollHint !== hint) {
            localScrollHint = hint;
            // Record the element's horizontal centre so the pill renders over the
            // right column (sidebar vs main content area).
            scrollHintXRef.current = r.left + r.width / 2;
            setScrollHint(hint);
          }
        }
      } else {
        ring.style.opacity = "0";
        if (localScrollHint !== null) {
          localScrollHint = null;
          setScrollHint(null);
        }
        if (localTargetFound && !hasOpenDialog) {
          localTargetFound = false;
          setTargetFound(false);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    setTargetFound(false);
    setScrollHint(null);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [active, step?.id, finished, ringTargetAttr, inSidebarNavMode, isMobile, navSidebarHref]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll target into view when first found ─────────────────────────────
  // scrollIntoView works for both the main content area and the sidebar's
  // own overflow container (the browser walks ancestor scroll boxes).
  const scrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!targetFound || !step) return;
    const activeTarget = (inSidebarNavMode && isMobile && navSidebarHref)
      ? (document.body.dataset.mobileSidebar === "open" ? `nav-${navSidebarHref}` : "mobile-menu-btn")
      : ringTargetAttr;
    const scrollKey = `${step.id}::${activeTarget}`;
    if (scrolledRef.current === scrollKey) return;
    scrolledRef.current = scrollKey;
    const el = document.querySelector<HTMLElement>(`[data-tour-target="${activeTarget}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      const fullyVisible = r.top >= 80 && r.bottom <= window.innerHeight - 80;
      if (!fullyVisible) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [targetFound, step?.id, ringTargetAttr]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;

  // ── Completion / cleanup screen ──────────────────────────────────────────
  if (finished) {
    return <TourCompletion onComplete={complete} hasDemoData={hasAnyDemoData(refs)} />;
  }

  if (!step) return null;

  const completionLabel = STEP_COMPLETE_LABELS[step.id] ?? "Step complete!";

  const tooltipCornerStyle = tooltipSide === "left"
    ? { bottom: 24, left: 24 }
    : { bottom: 24, right: 24 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: Z, pointerEvents: "none" }}>
      {/* ── Click-blocking quadrants ─────────────────────────────────────────
          The RAF loop drives these directly via cssText. When the target is
          visible they frame the spotlight cutout; when it's off-screen or a
          dialog is open they revert to pointer-events:none so the user can
          scroll / interact freely.  The tooltip card lives at Z+1 so it
          always receives pointer events even when it overlaps these divs. */}

      {/* Spotlight ring — GPU-composited to reduce scroll jitter */}
      <div
        ref={ringRef}
        style={{
          position: "absolute",
          top: 0, left: 0, width: 0, height: 0,
          borderRadius: "0.5rem",
          border: "2px solid hsl(var(--primary))",
          boxShadow: [
            "0 0 0 9999px rgba(10,15,30,0.28)",
            "0 0 0 3px hsl(var(--primary) / 0.3)",
            "0 0 20px 4px hsl(var(--primary) / 0.35)",
          ].join(", "),
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.2s ease",
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      />

      {/* Scroll hint arrow — anchored to the target element's horizontal column */}
      {scrollHint && !transition && !isDialogOpen && (
        <div
          style={{
            position: "fixed",
            [scrollHint === "up" ? "top" : "bottom"]: 80,
            // Use the recorded centre-X of the off-screen target so the hint
            // appears over the sidebar when the target is a sidebar nav item.
            left: scrollHintXRef.current,
            transform: "translateX(-50%)",
            zIndex: Z + 2,
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.25rem",
            animation: "bounce 1s infinite",
          }}
        >
          <div
            style={{
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              borderRadius: "9999px",
              padding: "0.4rem 0.8rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            {scrollHint === "up"
              ? <><ChevronUp style={{ width: "0.875rem", height: "0.875rem" }} /> Scroll up</>
              : <><ChevronDown style={{ width: "0.875rem", height: "0.875rem" }} /> Scroll down</>
            }
          </div>
        </div>
      )}

      {/* Page-transition overlay */}
      {transition && (
        <div
          style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "hsl(var(--card))", color: "hsl(var(--foreground))",
              borderRadius: "0.85rem", border: "1px solid hsl(var(--border))",
              boxShadow: "0 16px 48px -8px rgba(0,0,0,0.4)", padding: "1.5rem 2rem",
              textAlign: "center", maxWidth: "22rem",
              animation: "fadeInScale 0.25s ease",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem" }}>
              <CheckCheck style={{ width: "1.75rem", height: "1.75rem", color: "hsl(142, 71%, 45%)" }} />
            </div>
            <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: "0 0 0.35rem" }}>Step complete</p>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, margin: "0 0 0.2rem" }}>Next up</h3>
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>{transition.nextTitle}</p>
          </div>
        </div>
      )}

      {/* PDF preview close cue — shown when the preview dialog is open on the preview-pdf step */}
      {isDialogOpen && step?.id === "preview-pdf" && !transition && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: Z + 2,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            background: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
            borderRadius: "9999px",
            padding: "0.5rem 1rem",
            fontSize: "0.8125rem",
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          }}
        >
          Close the preview to continue
          <ArrowRight style={{ width: "0.875rem", height: "0.875rem" }} />
        </div>
      )}

      {/* Tooltip — fixed corner, hidden when a dialog is open */}
      {!transition && !isDialogOpen && (
        <div
          style={{
            position: "fixed",
            ...tooltipCornerStyle,
            width: TOOLTIP_W,
            maxWidth: "calc(100vw - 48px)",
            background: "hsl(var(--card))",
            borderRadius: "0.75rem",
            border: "1px solid hsl(var(--border))",
            boxShadow: "0 16px 48px -8px rgba(0,0,0,0.4)",
            padding: "1rem 1.1rem",
            pointerEvents: "auto",
            color: "hsl(var(--foreground))",
            zIndex: Z + 1,
          }}
        >
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.45rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <BookOpen style={{ width: "0.875rem", height: "0.875rem", color: "hsl(var(--primary))" }} />
              <span style={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))" }}>
                Step {index + 1} of {total}
              </span>
            </div>
            <button
              onClick={() => setExitPrompt(true)}
              title="Stop the walkthrough"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "1.5rem", height: "1.5rem", borderRadius: "0.375rem",
                border: "none", background: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "none"; }}
            >
              <X style={{ width: "0.875rem", height: "0.875rem" }} />
            </button>
          </div>

          {/* Progress bar */}
          <div style={{ display: "flex", gap: "0.2rem", marginBottom: "0.75rem" }}>
            {Array.from({ length: total }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: "0.25rem", flex: 1, borderRadius: "9999px",
                  background: i < index
                    ? "hsl(142, 71%, 45%)"
                    : i === index
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted))",
                  transition: "background 0.3s ease",
                }}
              />
            ))}
          </div>

          {/* Step content */}
          {pendingAdvance ? (
            /* ── Step-complete confirmation ── */
            <div style={{ textAlign: "center", padding: "0.25rem 0 0.5rem" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.5rem" }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "2.25rem", height: "2.25rem", borderRadius: "9999px",
                  background: "hsl(142, 71%, 45% / 0.12)",
                }}>
                  <CheckCircle2 style={{ width: "1.25rem", height: "1.25rem", color: "hsl(142, 71%, 45%)" }} />
                </div>
              </div>
              <p style={{ fontSize: "0.9375rem", fontWeight: 700, margin: "0 0 0.25rem" }}>
                ✓ {completionLabel}
              </p>
              <p style={{ fontSize: "0.8rem", color: "hsl(var(--muted-foreground))", margin: "0 0 0.85rem" }}>
                {index + 1 < total ? `Up next: ${GUIDED_TOUR_STEPS[index + 1]?.title ?? ""}` : "Final step complete!"}
              </p>
              <Button size="sm" onClick={confirmAdvance} style={{ gap: "0.35rem", width: "100%" }}>
                Continue <ArrowRight style={{ width: "0.8rem", height: "0.8rem" }} />
              </Button>
            </div>
          ) : inSidebarNavMode ? (
            /* ── Sidebar navigation prompt ── */
            <>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, margin: "0 0 0.3rem" }}>{step.title}</h3>
              <p style={{ fontSize: "0.8125rem", lineHeight: 1.5, color: "hsl(var(--muted-foreground))", margin: "0 0 0.75rem" }}>
                {isMobile
                  ? <>Tap the <strong>menu button</strong> at the bottom-left to open the sidebar, then tap <strong>{navLabel}</strong>.</>
                  : <>Click <strong>{navLabel}</strong> in the sidebar on the left to continue.</>
                }
              </p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                <button
                  onClick={prev}
                  disabled={index === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.3rem",
                    background: "none", border: "none", padding: "0.3rem 0.2rem",
                    cursor: index === 0 ? "default" : "pointer",
                    fontSize: "0.8125rem", fontWeight: 500,
                    color: index === 0 ? "hsl(var(--muted-foreground) / 0.4)" : "hsl(var(--muted-foreground))",
                  }}
                >
                  <ArrowLeft style={{ width: "0.8rem", height: "0.8rem" }} /> Back
                </button>
                {step.allowManualNext && (
                  <Button size="sm" onClick={next} style={{ gap: "0.35rem" }}>
                    Skip <ArrowRight style={{ width: "0.85rem", height: "0.85rem" }} />
                  </Button>
                )}
              </div>
            </>
          ) : (
            /* ── Normal step content ── */
            <>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, margin: "0 0 0.3rem" }}>{step.title}</h3>
              <p style={{ fontSize: "0.8125rem", lineHeight: 1.5, color: "hsl(var(--muted-foreground))", margin: "0 0 0.85rem" }}>
                {step.body}
              </p>

              {!targetFound && !scrollHint && (
                <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", margin: "0 0 0.6rem" }}>
                  Finding the right spot on this page…
                </p>
              )}

              {/* Multi-event progress (e.g. "1 of 2 added") */}
              {step.minEventCount && step.minEventCount > 1 && stepEventCount > 0 && stepEventCount < step.minEventCount && (
                <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "hsl(142, 71%, 45%)", margin: "0 0 0.6rem" }}>
                  ✓ {stepEventCount} of {step.minEventCount} done — add one more!
                </p>
              )}

              {/* Actions */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                <button
                  onClick={prev}
                  disabled={index === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.3rem",
                    background: "none", border: "none", padding: "0.3rem 0.2rem",
                    cursor: index === 0 ? "default" : "pointer",
                    fontSize: "0.8125rem", fontWeight: 500,
                    color: index === 0 ? "hsl(var(--muted-foreground) / 0.4)" : "hsl(var(--muted-foreground))",
                  }}
                >
                  <ArrowLeft style={{ width: "0.8rem", height: "0.8rem" }} /> Back
                </button>

                {step.advanceOn && !step.allowManualNext ? (
                  <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>
                    Complete the action to continue
                  </span>
                ) : (
                  <Button size="sm" onClick={next} style={{ gap: "0.35rem" }}>
                    Next <ArrowRight style={{ width: "0.85rem", height: "0.85rem" }} />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {exitPrompt && (
        <ExitPrompt
          onCancel={() => setExitPrompt(false)}
          onExit={() => { setExitPrompt(false); void exit(false); }}
        />
      )}
    </div>
  );
}

function hasAnyDemoData(refs: ReturnType<typeof useGuidedTour>["refs"]): boolean {
  return (
    refs.customerIds.length > 0 ||
    refs.productIds.length > 0 ||
    refs.projectIds.length > 0 ||
    refs.templateIds.length > 0 ||
    refs.quoteIds.length > 0 ||
    refs.invoiceIds.length > 0
  );
}

// ── Modal scaffold ──────────────────────────────────────────────────────────
function CenteredModal({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: Z + 10,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(10,15,30,0.5)", padding: "1rem", pointerEvents: "auto",
      }}
    >
      <div
        style={{
          width: "26rem", maxWidth: "100%",
          background: "hsl(var(--card))", color: "hsl(var(--foreground))",
          borderRadius: "0.85rem", border: "1px solid hsl(var(--border))",
          boxShadow: "0 24px 60px -12px rgba(0,0,0,0.45)", padding: "1.5rem",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ExitPrompt({
  onCancel,
  onExit,
}: {
  onCancel: () => void;
  onExit: () => void;
}) {
  return (
    <CenteredModal>
      <h3 style={{ fontSize: "1.05rem", fontWeight: 700, margin: "0 0 0.5rem" }}>Stop the walkthrough?</h3>
      <p style={{ fontSize: "0.85rem", lineHeight: 1.5, color: "hsl(var(--muted-foreground))", margin: "0 0 1.1rem" }}>
        Your setup checklist keeps track of what's left. Click "Show me" on any item to pick the walkthrough up again.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <Button onClick={onExit}>Stop walkthrough</Button>
        <Button variant="ghost" onClick={onCancel}>Keep going</Button>
      </div>
    </CenteredModal>
  );
}

function TourCompletion({
  hasDemoData,
  onComplete,
}: {
  hasDemoData: boolean;
  onComplete: (clearDemoData: boolean) => Promise<void>;
}) {
  const [, navigate] = useLocation();
  const [busy, setBusy] = useState(false);

  const finish = async (clear: boolean) => {
    setBusy(true);
    try {
      await onComplete(clear);
      navigate("/dashboard");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: Z + 10, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(10,15,30,0.55)", padding: "1rem", pointerEvents: "auto" }}>
      <div style={{ width: "28rem", maxWidth: "100%", background: "hsl(var(--card))", color: "hsl(var(--foreground))", borderRadius: "0.85rem", border: "1px solid hsl(var(--border))", boxShadow: "0 24px 60px -12px rgba(0,0,0,0.45)", padding: "1.75rem", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.85rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "3rem", height: "3rem", borderRadius: "9999px", background: "hsl(142, 71%, 45% / 0.12)" }}>
            <CheckCircle2 style={{ width: "1.75rem", height: "1.75rem", color: "hsl(142, 71%, 45%)" }} />
          </div>
        </div>
        <h3 style={{ fontSize: "1.2rem", fontWeight: 700, margin: "0 0 0.5rem" }}>You're all set!</h3>
        <p style={{ fontSize: "0.875rem", lineHeight: 1.55, color: "hsl(var(--muted-foreground))", margin: "0 0 1.25rem" }}>
          You've run a job from quote to payment.{" "}
          {hasDemoData
            ? "Want a clean slate for your real work, or keep the sample records for reference? Your business details and logo are always kept."
            : "Head to your dashboard to start your first real job."}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {hasDemoData ? (
            <>
              <Button disabled={busy} onClick={() => finish(true)}>Remove sample data and go to dashboard</Button>
              <Button variant="outline" disabled={busy} onClick={() => finish(false)}>Keep sample data and go to dashboard</Button>
            </>
          ) : (
            <Button disabled={busy} onClick={() => finish(false)}>Go to dashboard</Button>
          )}
        </div>
      </div>
    </div>
  );
}
